import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryTwoToneIcon from "@mui/icons-material/CategoryTwoTone";
import CloseIcon from "@mui/icons-material/Close";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import ShareIcon from "@mui/icons-material/Share";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TwitterIcon from "@mui/icons-material/Twitter";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { LoadingButton } from "@mui/lab";
import {
	Alert,
	alpha,
	Autocomplete,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { createFormData } from "nhb-toolbox";
import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import CommonFileUploader from "src/common/CommonFileUploader";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import type {
	IOfflineExhibitorDetails,
	ISocialMedia,
	PriceType,
} from "../type/offlineExhibitorType";
import ServiceItemRow from "./ServiceItemRow";
import StallItemRow from "./StallItemRow";

const CATEGORY_SUGGESTIONS = [
	"Tourism",
	"Travel Agency",
	"Aviation",
	"Airlines",
	"Tour Operator",
	"Hospitality",
	"Hotel & Resort",
	"Hajj & Umrah",
	"Transport & Rent-a-Car",
	"Eco Tourism",
	"Cruise & Boat",
	"Visa & Immigration",
	"IT & Technology",
	"Government & Association",
];

interface Props {
	open: boolean;
	handleClose: () => void;
	exhibitorId: number | null;
}

interface FormValues {
	name: string;
	email: string;
	password: string;
	company_email: string;
	website: string;
	category: string[];
	social_media: {
		facebook: string;
		linkedin: string;
		twitter: string;
		instagram: string;
		youtube: string;
	};
	map: string;
	stalls: {
		hall_name: string;
		stall_no: string;
	}[];
	services: {
		service_name: string;
		price_type: PriceType;
		price: string;
		service_description: string;
	}[];
}

const UpdateOfflineExhibitor: React.FC<Props> = ({
	open,
	handleClose,
	exhibitorId,
}) => {
	const [apiError, setApiError] = useState<string | null>(null);
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [logoFile, setLogoFile] = useState<File | null>(null);
	const [brochureFile, setBrochureFile] = useState<File | null>(null);
	const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null);
	const [existingBrochureUrl, setExistingBrochureUrl] = useState<string | null>(null);

	const { data: exhibitor, isLoading: isFetching } =
		useApiQuery<IOfflineExhibitorDetails>({
			endPoint: `/api/v1/admin/offline-exhibitor/${exhibitorId}`,
			queryKeys: ["OfflineExhibitorDetails", exhibitorId ?? 0],
			enabled: Boolean(open && exhibitorId),
		});

	const {
		register,
		control,
		handleSubmit,
		reset,
		setValue,
		getValues,
		watch,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			name: "",
			email: "",
			password: "",
			company_email: "",
			website: "",
			category: [],
			social_media: {
				facebook: "",
				linkedin: "",
				twitter: "",
				instagram: "",
				youtube: "",
			},
			map: "",
			stalls: [{ hall_name: "", stall_no: "" }],
			services: [],
		},
	});

	const {
		fields: stallFields,
		append: appendStall,
		remove: removeStall,
	} = useFieldArray({
		control,
		name: "stalls",
	});

	const {
		fields: serviceFields,
		append: appendService,
		remove: removeService,
	} = useFieldArray({
		control,
		name: "services",
	});

	useEffect(() => {
		if (exhibitor && open) {
			setApiError(null);
			setLogoFile(null);
			setBrochureFile(null);
			setShowPassword(false);
			setExistingLogoUrl(exhibitor.logo || null);
			setExistingBrochureUrl(exhibitor.brochure || null);

			let parsedCategories: string[] = [];
			if (Array.isArray(exhibitor.category)) {
				parsedCategories = exhibitor.category;
			} else if (typeof exhibitor.category === "string" && exhibitor.category.trim()) {
				try {
					const parsed = JSON.parse(exhibitor.category);
					if (Array.isArray(parsed)) parsedCategories = parsed;
					else if (typeof parsed === "string") parsedCategories = [parsed];
				} catch {
					parsedCategories = exhibitor.category
						.split(",")
						.map((c) => c.trim())
						.filter(Boolean);
				}
			}

			const parsedSocialMedia: FormValues["social_media"] = {
				facebook: "",
				linkedin: "",
				twitter: "",
				instagram: "",
				youtube: "",
			};
			if (exhibitor.social_media) {
				if (typeof exhibitor.social_media === "object") {
					const sm = exhibitor.social_media as ISocialMedia;
					parsedSocialMedia.facebook = sm.facebook || "";
					parsedSocialMedia.linkedin = sm.linkedin || "";
					parsedSocialMedia.twitter = sm.twitter || "";
					parsedSocialMedia.instagram = sm.instagram || "";
					parsedSocialMedia.youtube = sm.youtube || "";
				} else if (typeof exhibitor.social_media === "string" && exhibitor.social_media.trim()) {
					try {
						const sm = JSON.parse(exhibitor.social_media);
						parsedSocialMedia.facebook = sm.facebook || "";
						parsedSocialMedia.linkedin = sm.linkedin || "";
						parsedSocialMedia.twitter = sm.twitter || "";
						parsedSocialMedia.instagram = sm.instagram || "";
						parsedSocialMedia.youtube = sm.youtube || "";
					} catch {
						// ignore
					}
				}
			}

			reset({
				name: exhibitor.name || "",
				email: exhibitor.email || "",
				password: "",
				company_email: exhibitor.company_email || "",
				website: exhibitor.website || "",
				category: parsedCategories,
				social_media: parsedSocialMedia,
				map: exhibitor.map || "",
				stalls:
					exhibitor.stalls && exhibitor.stalls.length > 0
						? exhibitor.stalls.map((s) => ({
							hall_name: s.hall_name || "",
							stall_no: s.stall_no || "",
						}))
						: [{ hall_name: "", stall_no: "" }],
				services:
					exhibitor.services && exhibitor.services.length > 0
						? exhibitor.services.map((srv) => {
							let inferredType: PriceType = "fixed";
							if (srv.price_type) {
								inferredType = srv.price_type as PriceType;
							} else if (srv.price?.includes("-")) {
								inferredType = "range";
							} else if (srv.price?.trim().toLowerCase() === "negotiable") {
								inferredType = "negotiable";
							}
							return {
								service_name: srv.service_name || "",
								price_type: inferredType,
								price: srv.price || "",
								service_description: srv.service_description || "",
							};
						})
						: [],
			});
			setValue("password", "");
		}
	}, [exhibitor, open, reset, setValue]);

	const { mutate: updateExhibitor, isPending: isUpdating } = useApiMutation<any, any>({
		method: "PATCH",
		endPoint: `/api/v1/admin/offline-exhibitor/${exhibitorId}`,
		invalidateQueries: ["OfflineExhibitors", "OfflineExhibitorDetails"],
	});

	const onSubmit = (data: FormValues) => {
		setApiError(null);
		const cleanedStalls = data.stalls.filter(
			(s) => s.hall_name.trim() !== "" || s.stall_no.trim() !== ""
		);
		const cleanedServices = data.services
			.filter(
				(s) =>
					s.service_name.trim() !== "" ||
					s.price.trim() !== "" ||
					s.service_description.trim() !== ""
			)
			.map((s) => ({
				service_name: s.service_name.trim(),
				price_type: s.price_type || "fixed",
				price: s.price.trim(),
				service_description: s.service_description?.trim() || "",
			}));

		const cleanedCategories = (data.category || []).filter(
			(c) => typeof c === "string" && c.trim() !== ""
		);

		const cleanedSocialMedia: Record<string, string> = {};
		if (data.social_media) {
			Object.entries(data.social_media).forEach(([key, val]) => {
				if (val && typeof val === "string" && val.trim() !== "") {
					cleanedSocialMedia[key] = val.trim();
				}
			});
		}

		const payloadData: Record<string, any> = {
			name: data.name.trim(),
			email: data.email?.trim() || undefined,
			company_email: data.company_email?.trim() || undefined,
			website: data.website?.trim() || undefined,
			category: cleanedCategories.length > 0 ? cleanedCategories : undefined,
			social_media:
				Object.keys(cleanedSocialMedia).length > 0
					? cleanedSocialMedia
					: undefined,
			map: data.map?.trim() || undefined,
			stalls: cleanedStalls,
			services: cleanedServices,
		};

		// Only include password if a new password was provided
		if (data.password && data.password.trim() !== "") {
			payloadData.password = data.password.trim();
		}

		if (logoFile) {
			payloadData.logo = logoFile;
		}
		if (brochureFile) {
			payloadData.brochure = brochureFile;
		}

		const payload = createFormData(payloadData);

		updateExhibitor(payload, {
			onSuccess: () => {
				setApiError(null);
				handleClose();
			},
			onError: (err: any) => {
				const errorMsg =
					err?.response?.data?.message ||
					err?.message ||
					"Failed to update exhibitor. Please check the inputs.";
				setApiError(errorMsg);
			},
		});
	};

	const handleModalClose = () => {
		if (!isUpdating) {
			setApiError(null);
			setLogoFile(null);
			setBrochureFile(null);
			setShowPassword(false);
			reset();
			handleClose();
		}
	};

	const currentCategories = watch("category") || [];

	return (
		<Dialog
			open={open}
			onClose={handleModalClose}
			disableEnforceFocus
			maxWidth="md"
			fullWidth
			scroll="paper"
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Typography variant="h4" fontWeight="bold">
					Update Offline Exhibitor
				</Typography>
				<IconButton onClick={handleModalClose} size="small" disabled={isUpdating}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<Divider />

			<Box
				component="form"
				onSubmit={handleSubmit(onSubmit)}
				noValidate
				autoComplete="off"
				sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
			>
				<DialogContent dividers sx={{ py: 2.5 }}>
					{isFetching ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: 250,
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<Grid container spacing={2.5}>
							{/* Error Alert Display */}
							{apiError && (
								<Grid item xs={12}>
									<Alert
										severity="error"
										onClose={() => setApiError(null)}
										sx={{ width: "100%", borderRadius: 1.5 }}
									>
										{apiError}
									</Alert>
								</Grid>
							)}

							{/* Section 1: Basic & Account Information */}
							<Grid item xs={12}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
									<BusinessIcon color="primary" fontSize="small" />
									<Typography variant="h5" fontWeight="bold">
										Basic & Account Information
									</Typography>
								</Box>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									label="Exhibitor / Company Name"
									fullWidth
									required
									{...register("name", {
										required: "Exhibitor name is required",
									})}
									error={!!errors.name}
									helperText={errors.name?.message}
									placeholder="e.g. Novoair Limited"
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									label="Account / Login Email"
									fullWidth
									required
									type="email"
									{...register("email", {
										required: "Login email is required",
										pattern: {
											value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
											message: "Invalid email format",
										},
									})}
									error={!!errors.email}
									helperText={errors.email?.message}
									placeholder="e.g. srabon2@yopmail.com"
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									label="New Password"
									fullWidth
									type={showPassword ? "text" : "password"}
									autoComplete="new-password"
									inputProps={{
										autoComplete: "new-password",
									}}
									{...register("password", {
										required: false,
										validate: (val) => {
											if (!val || val.trim() === "") return true;
											if (val.length < 6) {
												return "Password must be at least 6 characters";
											}
											return true;
										},
									})}
									error={!!errors.password}
									placeholder="Leave blank to keep unchanged"
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={() => setShowPassword((prev) => !prev)}
													edge="end"
													size="small"
												>
													{showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									label="Company Email"
									fullWidth
									type="email"
									{...register("company_email", {
										pattern: {
											value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
											message: "Invalid email format",
										},
									})}
									error={!!errors.company_email}
									helperText={errors.company_email?.message}
									placeholder="e.g. srabon@gmail.com"
								/>
							</Grid>

							<Grid item xs={12}>
								<TextField
									label="Website URL"
									fullWidth
									{...register("website")}
									placeholder="e.g. https://flynovoair.com"
									error={!!errors.website}
									helperText={errors.website?.message}
								/>
							</Grid>

							{/* Section 2: Dedicated Categories Section */}
							<Grid item xs={12}>
								<Paper
									variant="outlined"
									sx={{
										p: 2,
										borderRadius: 2,
										bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
										border: "1px solid",
										borderColor: "divider",
									}}
								>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											flexWrap: "wrap",
											gap: 1,
											mb: 1.5,
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<CategoryTwoToneIcon color="primary" fontSize="small" />
											<Typography variant="h5" fontWeight="bold">
												Exhibitor Categories & Industry Tags
											</Typography>
											<Chip
												label={`${currentCategories.length} selected`}
												size="small"
												color={currentCategories.length > 0 ? "primary" : "default"}
												sx={{ fontWeight: 700, height: 22, fontSize: "0.75rem", borderRadius: 1 }}
											/>
										</Box>
										<Typography variant="caption" color="text.secondary">
											Click popular tags below or type custom & press Enter
										</Typography>
									</Box>

									{/* Quick Preset Suggestion Chips */}
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2 }}>
										{CATEGORY_SUGGESTIONS.map((suggestion) => {
											const isSelected = currentCategories.includes(suggestion);
											return (
												<Chip
													key={suggestion}
													label={suggestion}
													size="small"
													clickable
													color={isSelected ? "primary" : "default"}
													variant={isSelected ? "filled" : "outlined"}
													onClick={() => {
														if (isSelected) {
															setValue(
																"category",
																currentCategories.filter((c) => c !== suggestion),
																{ shouldValidate: true }
															);
														} else {
															setValue("category", [...currentCategories, suggestion], {
																shouldValidate: true,
															});
														}
													}}
													sx={{
														borderRadius: 1.5,
														fontWeight: isSelected ? 700 : 500,
														transition: "all 0.15s ease-in-out",
														"&:hover": {
															transform: "scale(1.03)",
														},
													}}
												/>
											);
										})}
									</Box>

									{/* Autocomplete Multi-select with FreeSolo */}
									<Autocomplete
										multiple
										freeSolo
										options={CATEGORY_SUGGESTIONS}
										value={currentCategories}
										onChange={(_, newValue) => {
											setValue("category", newValue as string[], {
												shouldValidate: true,
											});
										}}
										renderTags={(value: readonly string[], getTagProps) =>
											value.map((option: string, index: number) => (
												<Chip
													variant="filled"
													color="primary"
													size="small"
													label={option}
													{...getTagProps({ index })}
													key={option + index}
													sx={{ borderRadius: 1, fontWeight: 600 }}
												/>
											))
										}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Add / Search Categories"
												placeholder="Type custom category & press Enter..."
												helperText="Multiple categories supported. Add as many as needed."
											/>
										)}
									/>
								</Paper>
							</Grid>

							{/* Section 2: Media & Attachments */}
							<Grid item xs={12}>
								<Divider sx={{ my: 1 }} />
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 1 }}>
									<AttachFileIcon color="primary" fontSize="small" />
									<Typography variant="h5" fontWeight="bold">
										Logo & Brochure
									</Typography>
								</Box>
							</Grid>

							<Grid item xs={12} sm={6}>
								<CommonFileUploader
									label="Company Logo"
									accept="image/*"
									selectedFile={logoFile}
									existingUrl={existingLogoUrl}
									onFileChange={(file) => setLogoFile(file)}
									helperText="Upload new image to replace current logo"
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<CommonFileUploader
									label="Company Brochure"
									accept=".pdf,application/pdf,image/*"
									selectedFile={brochureFile}
									existingUrl={existingBrochureUrl}
									onFileChange={(file) => setBrochureFile(file)}
									helperText="Upload new PDF or image to replace brochure"
								/>
							</Grid>

							{/* Section 3: Social Media Links */}
							<Grid item xs={12}>
								<Divider sx={{ my: 1 }} />
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 1 }}>
									<ShareIcon color="primary" fontSize="small" />
									<Typography variant="h5" fontWeight="bold">
										Social Media Links
									</Typography>
								</Box>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									label="Facebook URL"
									fullWidth
									size="small"
									placeholder="https://facebook.com/novoair"
									{...register("social_media.facebook")}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<FacebookIcon color="primary" fontSize="small" />
											</InputAdornment>
										),
									}}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									label="LinkedIn URL"
									fullWidth
									size="small"
									placeholder="https://linkedin.com/company/novoair"
									{...register("social_media.linkedin")}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<LinkedInIcon color="primary" fontSize="small" />
											</InputAdornment>
										),
									}}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									label="Twitter / X URL"
									fullWidth
									size="small"
									placeholder="https://twitter.com/novoair"
									{...register("social_media.twitter")}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<TwitterIcon color="primary" fontSize="small" />
											</InputAdornment>
										),
									}}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									label="Instagram URL"
									fullWidth
									size="small"
									placeholder="https://instagram.com/novoair"
									{...register("social_media.instagram")}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<InstagramIcon color="primary" fontSize="small" />
											</InputAdornment>
										),
									}}
								/>
							</Grid>

							<Grid item xs={12}>
								<TextField
									label="YouTube URL"
									fullWidth
									size="small"
									placeholder="https://youtube.com/@novoair"
									{...register("social_media.youtube")}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<YouTubeIcon color="error" fontSize="small" />
											</InputAdornment>
										),
									}}
								/>
							</Grid>

							{/* Section 4: Stalls Section */}
							<Grid item xs={12}>
								<Divider sx={{ my: 1 }} />
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										mb: 1.5,
										mt: 1,
									}}
								>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<StorefrontIcon color="primary" fontSize="small" />
										<Typography variant="h5" fontWeight="bold">
											Stall Information
										</Typography>
									</Box>
									<Button
										type="button"
										size="small"
										variant="outlined"
										startIcon={<AddCircleOutlineIcon />}
										onClick={() =>
											appendStall({
												hall_name: getValues("stalls.0.hall_name") || "",
												stall_no: "",
											})
										}
									>
										Add Stall
									</Button>
								</Box>

								{stallFields.map((field, index) => (
									<StallItemRow
										key={field.id}
										fieldId={field.id}
										index={index}
										control={control as any}
										register={register as any}
										errors={errors as any}
										removeStall={removeStall}
										canDelete={stallFields.length > 1}
									/>
								))}
							</Grid>

							{/* Section 5: Services Section */}
							<Grid item xs={12}>
								<Divider sx={{ my: 1 }} />
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										mb: 1.5,
										mt: 1,
									}}
								>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<MiscellaneousServicesIcon color="primary" fontSize="small" />
										<Typography variant="h5" fontWeight="bold">
											Services Information
										</Typography>
										<Typography variant="caption" color="text.secondary">
											(Optional)
										</Typography>
									</Box>
									<Button
										type="button"
										size="small"
										variant="outlined"
										startIcon={<AddCircleOutlineIcon />}
										onClick={() =>
											appendService({
												service_name: "",
												price_type: "fixed",
												price: "",
												service_description: "",
											})
										}
									>
										Add Service
									</Button>
								</Box>

								{serviceFields.length === 0 ? (
									<Box
										sx={{
											p: 2,
											textAlign: "center",
											border: "1px dashed",
											borderColor: "divider",
											borderRadius: 1.5,
											color: "text.secondary",
											bgcolor: "action.hover",
										}}
									>
										<Typography variant="body2">
											No services added yet (Optional). Click <b>Add Service</b> if you want to add services.
										</Typography>
									</Box>
								) : (
									serviceFields.map((field, index) => (
										<ServiceItemRow
											key={field.id}
											fieldId={field.id}
											index={index}
											control={control as any}
											register={register as any}
											errors={errors as any}
											removeService={removeService}
											canDelete={true}
										/>
									))
								)}
							</Grid>
						</Grid>
					)}
				</DialogContent>

				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						type="button"
						onClick={handleModalClose}
						variant="outlined"
						disabled={isUpdating}
						sx={{
							borderColor: "grey.400",
							color: "grey.700",
							"&:hover": { backgroundColor: "grey.100" },
						}}
					>
						Cancel
					</Button>
					<LoadingButton
						type="submit"
						variant="contained"
						color="primary"
						loading={isUpdating}
						disabled={isFetching}
					>
						<span>Update Exhibitor</span>
					</LoadingButton>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default UpdateOfflineExhibitor;
